import { Object3D, Mesh, Data3DTexture, RGBAFormat, UnsignedByteType, LinearFilter, ClampToEdgeWrapping } from 'three'

export type RGBTransform = (r: number, g: number, b: number) => [number, number, number]

const clamp01 = (v: number) => Math.min(1, Math.max(0, v))

export const buildLUT = (size: number, transform: RGBTransform): Data3DTexture => {
  const data = new Uint8Array(size * size * size * 4)
  let i = 0
  for (let z = 0; z < size; z++) {
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const [or, og, ob] = transform(x / (size - 1), y / (size - 1), z / (size - 1))
        data[i++] = Math.round(clamp01(or) * 255)
        data[i++] = Math.round(clamp01(og) * 255)
        data[i++] = Math.round(clamp01(ob) * 255)
        data[i++] = 255
      }
    }
  }
  const tex = new Data3DTexture(data, size, size, size)
  tex.format = RGBAFormat
  tex.type = UnsignedByteType
  tex.minFilter = LinearFilter
  tex.magFilter = LinearFilter
  tex.wrapS = ClampToEdgeWrapping
  tex.wrapT = ClampToEdgeWrapping
  tex.wrapR = ClampToEdgeWrapping
  tex.needsUpdate = true
  return tex
}

export const identity: RGBTransform = (r, g, b) => [r, g, b]

export const hueMatrix = (deg: number) => {
  const rad = (deg * Math.PI) / 180
  const c = Math.cos(rad)
  const s = Math.sin(rad)
  return [
    [0.299 + 0.701 * c + 0.168 * s, 0.587 - 0.587 * c + 0.330 * s, 0.114 - 0.114 * c - 0.497 * s],
    [0.299 - 0.299 * c - 0.328 * s, 0.587 + 0.413 * c + 0.035 * s, 0.114 - 0.114 * c + 0.292 * s],
    [0.299 - 0.3 * c + 1.25 * s, 0.587 - 0.588 * c - 1.05 * s, 0.114 + 0.886 * c - 0.203 * s],
  ] as const
}

export type CinematicParams = {
  exposure: number
  contrast: number
  saturation: number
  temperature: number
  tint: number
  hue: number
  gamma: number
  lift: number
}

export const buildCinematic = (p: CinematicParams): RGBTransform => {
  const m = hueMatrix(p.hue)
  const invGamma = 1 / Math.max(0.0001, p.gamma)

  return (r, g, b) => {
    r = Math.pow(r, 0.95) * 1.02 + 0.02
    g = Math.pow(g, 1.0) * 0.98 + 0.016
    b = Math.pow(b, 1.1) * 0.95 + 0.024

    r *= p.exposure
    g *= p.exposure
    b *= p.exposure

    r += p.temperature * 0.1
    b -= p.temperature * 0.1
    g += p.tint * 0.1

    r += p.lift
    g += p.lift
    b += p.lift

    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b
    r = lum + (r - lum) * p.saturation
    g = lum + (g - lum) * p.saturation
    b = lum + (b - lum) * p.saturation

    r = (r - 0.5) * p.contrast + 0.5
    g = (g - 0.5) * p.contrast + 0.5
    b = (b - 0.5) * p.contrast + 0.5

    if (p.hue !== 0) {
      const nr = m[0][0] * r + m[0][1] * g + m[0][2] * b
      const ng = m[1][0] * r + m[1][1] * g + m[1][2] * b
      const nb = m[2][0] * r + m[2][1] * g + m[2][2] * b
      r = nr; g = ng; b = nb
    }

    r = Math.pow(Math.max(0, r), invGamma)
    g = Math.pow(Math.max(0, g), invGamma)
    b = Math.pow(Math.max(0, b), invGamma)

    return [r, g, b]
  }
}

export const LUT_SIZE = 32

export const defaultLutTexture = buildLUT(LUT_SIZE, buildCinematic({
  exposure: 1.0,
  contrast: 1.0,
  saturation: 1.0,
  hue: 0,
  temperature: 0.0,
  tint: 0.0,
  gamma: 1.0,
  lift: 0.0,
}))

export const TARGET_FRAMERATE = 60

// Each marker maps to the camera key (e.g. 'cam7') it's shown on, or a list of
// keys to show it on several cameras.
export const markerVisibility: Record<string, string | string[]> = {
  picture: ['cam1', 'cam11'],
  gazeta: 'cam6',
  lampabudzik: 'cam6',
  desk: 'cam4',
  wojskowy: 'cam7',
  dyndalski: 'cam7',
  fotel: 'cam6',
  rodzinne: 'cam7',
  zofia: 'cam7',
  grafiki: 'cam7',
  budzik: 'cam7',
  raczka: 'cam7',
  duchowka: 'cam8',
  archiveBooks: 'cam10',
  wozek: 'cam10',
  kule: 'cam10',
  fredro: 'cam10',
  jacek: 'cam4',
  tygodnik: 'cam6',
  panczykowski: 'cam7',
  blogoslawienstwa: 'cam4',
  okno: 'cam3',
  credits: 'cam3'
}

// Whether the interactable named `objName` is active on the camera `activeCamKey`
// (e.g. 'cam1'). Objects with no markerVisibility entry are active everywhere.
export function isMarkerVisibleOnCam(objName: string, activeCamKey: string): boolean {
  const entry = Object.entries(markerVisibility).find(([key]) => objName.includes(key))
  if (!entry) return true
  const cams = entry[1]
  return Array.isArray(cams) ? cams.includes(activeCamKey) : cams === activeCamKey
}

// In development the CRA dev-server proxy (setupProxy.js) rewrites
// '/proxy/muzeum' to the muzeum domain while stripping X-Frame-Options.
// Production builds have no such proxy, so we point directly at the
// muzeum domain there instead.
const muzeumUrl = (room: string) =>
  process.env.NODE_ENV === 'production'
    ? `https://api.muzeum.dom-komedii.pl/${room}/`
    : `/proxy/muzeum/${room}/`

export type MarkerURL = string | { url: string; anim: string[]; callback?: () => void }

export const markerURL: Record<string, MarkerURL> = {
  picture: muzeumUrl('pokoj_01'),
  gazeta: muzeumUrl('pokoj_02'),
  lampabudzik: muzeumUrl('pokoj_03'),
  desk: muzeumUrl('pokoj_04'),
  wojskowy: muzeumUrl('pokoj_05'),
  dyndalski: muzeumUrl('pokoj_06'),
  fotel: muzeumUrl('pokoj_17'),
  rodzinne: muzeumUrl('pokoj_09'),
  zofia: muzeumUrl('pokoj_11'),
  grafiki: muzeumUrl('pokoj_08'),
  budzik: muzeumUrl('pokoj_19'),
  raczka: { url: muzeumUrl('pokoj_10'), anim: ['pasekAction'] },
  duchowka: { url: muzeumUrl('pokoj_12'), anim: ['duchowkaAction', 'questionMarkAction'] },
  archiveBooks: muzeumUrl('pokoj_13'),
  wozek: muzeumUrl('pokoj_16'),
  kule: muzeumUrl('pokoj_15'),
  fredro: muzeumUrl('pokoj_14'),
  jacek: muzeumUrl('pokoj_20'),
  tygodnik: muzeumUrl('pokoj_23'),
  panczykowski: muzeumUrl('pokoj_07'),
  blogoslawienstwa: muzeumUrl('pokoj_21'),
  okno: muzeumUrl('pokoj_18'),
  credits: muzeumUrl('credits')
}

export const INIT_ANIMATIONS = [
  'doors&windows.002Action',
  'doors&windows.003Action',
  'cam1Action',
]

export interface CamData {
  object: Object3D
  fov: number
  near: number
  maxR: number
}

export function extractCameras(scene: Object3D): CamData[] {
  const cams: { name: string; data: CamData }[] = []
  scene.traverse((child) => {
    if (/^cam\d+$/i.test(child.name)) {
      const fov = (child.userData?.fov as number) ?? 50
      const near = (child.userData?.near as number) ?? 0.01
      const maxR = (child.userData?.maxR as number) ?? 45
      cams.push({
        name: child.name,
        data: { object: child, fov, near, maxR },
      })
    }
  })
  cams.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
  return cams.map((c) => c.data)
}

export interface ProxyData {
  name: string
  position: [number, number, number]
  targetCam: number
  label: string
}

export function extractInteractables(scene: Object3D): Object3D[] {
  const result: Object3D[] = []
  scene.traverse((child) => {
    if (!child.userData?.resourceId) return
    if ((child as Mesh).isMesh) {
      result.push(child)
    } else {
      let i = 0
      child.traverse((descendant) => {
        if ((descendant as Mesh).isMesh) {
          descendant.name = `${child.name}_${i++}`
          result.push(descendant)
        }
      })
    }
  })
  return result
}

export function extractProxies(scene: Object3D): ProxyData[] {
  const proxies: ProxyData[] = []
  scene.traverse((child) => {
    const match = child.name.match(/^cam(\d+)_proxy$/i)
    if (match) {
      const camIndex = parseInt(match[1], 10) - 1
      const label = (child.userData?.name as string) ?? `Cam${camIndex + 1}`
      proxies.push({
        name: child.name,
        position: [child.position.x, child.position.y, child.position.z],
        targetCam: camIndex,
        label,
      })
    }
  })
  return proxies
}
